import { formatDistance } from '../../measure/distance.js';
import type { MeasurementProjection, MeasurementsSnapshot } from '../../measure/types.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface MeasurementOverlayProps {
  snapshot: MeasurementsSnapshot;
  projections: readonly MeasurementProjection[];
  distances: readonly number[];
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const DOT_RADIUS = 7;

interface SegmentRefs {
  line: SVGLineElement;
  pillWrap: HTMLDivElement;
  pillText: HTMLSpanElement;
}

export function mountMeasurementOverlay(
  root: HTMLElement,
  initial: MeasurementOverlayProps,
): ComponentInstance<MeasurementOverlayProps> {
  // Wrap is fixed full-viewport; svg overlays absolute below the panel
  // (z-index:11). pointer-events:none so it doesn't intercept clicks.
  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('aria-hidden', 'true'); // distances announced via panel
  wrap.style.cssText = 'position:fixed;inset:0;z-index:11;pointer-events:none;display:none';

  // Single SVG for lines + circles. Pills are sibling HTML (better text
  // styling cross-browser than SVG <text>).
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.style.cssText = 'position:absolute;inset:0;display:block;pointer-events:none';
  wrap.appendChild(svg);

  const pillsContainer = document.createElement('div');
  pillsContainer.style.cssText = 'position:absolute;inset:0;pointer-events:none';
  wrap.appendChild(pillsContainer);

  root.appendChild(wrap);

  // Pools: one circle per point, one line + pill per segment. We grow
  // arrays on demand and hide unused entries (no remove/recreate per
  // frame so the GC stays quiet on per-frame projection updates).
  const dotPool: SVGCircleElement[] = [];
  const segmentPool: SegmentRefs[] = [];

  const ensureDot = (idx: number): SVGCircleElement => {
    let dot = dotPool[idx];
    if (dot === undefined) {
      dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('r', String(DOT_RADIUS));
      dot.setAttribute('fill', '#f59e0b');
      dot.setAttribute('stroke', 'white');
      dot.setAttribute('stroke-width', '2');
      svg.appendChild(dot);
      dotPool[idx] = dot;
    }
    return dot;
  };

  const ensureSegment = (idx: number): SegmentRefs => {
    let seg = segmentPool[idx];
    if (seg === undefined) {
      const line = document.createElementNS(SVG_NS, 'line');
      line.setAttribute('stroke', '#f59e0b');
      line.setAttribute('stroke-width', '2');
      line.setAttribute('stroke-dasharray', '8 4');
      svg.insertBefore(line, svg.firstChild); // lines below dots
      const pillWrap = document.createElement('div');
      pillWrap.style.cssText =
        'position:absolute;background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);color:#fcd34d;border:1px solid rgba(252,211,77,0.30);border-radius:9999px;padding:0.25rem 0.625rem;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;font-weight:700;transform:translate(-50%, -50%);pointer-events:none;white-space:nowrap';
      const pillText = document.createElement('span');
      pillWrap.appendChild(pillText);
      pillsContainer.appendChild(pillWrap);
      seg = { line, pillWrap, pillText };
      segmentPool[idx] = seg;
    }
    return seg;
  };

  const apply = (props: MeasurementOverlayProps): void => {
    const visible = props.snapshot.active && props.projections.length > 0;
    wrap.style.display = visible ? 'block' : 'none';
    if (!visible) return;

    // Position dots.
    props.projections.forEach((proj, idx) => {
      const dot = ensureDot(idx);
      if (proj.visible) {
        dot.setAttribute('cx', proj.screenX.toFixed(2));
        dot.setAttribute('cy', proj.screenY.toFixed(2));
        dot.setAttribute('display', 'block');
      } else {
        dot.setAttribute('display', 'none');
      }
    });
    // Hide leftover dots.
    for (let i = props.projections.length; i < dotPool.length; i++) {
      dotPool[i]?.setAttribute('display', 'none');
    }

    // Position segments (line + midpoint pill).
    for (let i = 0; i < props.distances.length; i++) {
      const a = props.projections[i];
      const b = props.projections[i + 1];
      const seg = ensureSegment(i);
      if (a === undefined || b === undefined || !a.visible || !b.visible) {
        seg.line.setAttribute('display', 'none');
        seg.pillWrap.style.display = 'none';
        continue;
      }
      seg.line.setAttribute('x1', a.screenX.toFixed(2));
      seg.line.setAttribute('y1', a.screenY.toFixed(2));
      seg.line.setAttribute('x2', b.screenX.toFixed(2));
      seg.line.setAttribute('y2', b.screenY.toFixed(2));
      seg.line.setAttribute('display', 'block');
      const midX = (a.screenX + b.screenX) / 2;
      const midY = (a.screenY + b.screenY) / 2;
      seg.pillWrap.style.left = `${midX.toFixed(0)}px`;
      seg.pillWrap.style.top = `${midY.toFixed(0)}px`;
      seg.pillWrap.style.display = 'block';
      const distance = props.distances[i];
      if (distance !== undefined) {
        setText(seg.pillText, formatDistance(distance, props.snapshot.units));
      }
    }
    // Hide leftover segments.
    for (let i = props.distances.length; i < segmentPool.length; i++) {
      const seg = segmentPool[i];
      if (seg !== undefined) {
        seg.line.setAttribute('display', 'none');
        seg.pillWrap.style.display = 'none';
      }
    }
  };

  apply(initial);

  return {
    update(props: MeasurementOverlayProps) {
      apply(props);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
